import React, { Children, cloneElement, createContext, isValidElement, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Group, Modal as MantineModal, UnstyledButton, Text } from '@mantine/core';
import { Button } from './Controls';

const DialogStackContext = createContext(null);
export function DialogStack({ children }) {
  const [ids, setIds] = useState([]);
  const add = useCallback((id) => setIds((previous) => previous.includes(id) ? previous : [...previous, id]), []);
  const remove = useCallback((id) => setIds((previous) => previous.filter((item) => item !== id)), []);
  const value = useMemo(() => ({ ids, add, remove }), [ids, add, remove]);
  return <DialogStackContext.Provider value={value}>{children}</DialogStackContext.Provider>;
}

// Compound parts let the existing screens keep their conditional dialog content.
const Header = ({ children }) => children;
const Content = ({ scrolling, children, className = '', ...props }) => <Box {...props} className={`tour-dialog-body ${className}`}>{children}</Box>;
const Actions = ({ children, ...props }) => <Group {...props} justify="flex-end" gap="xs" className="tour-dialog-actions">{children}</Group>;

export default function Dialog({ open, onClose, trigger, header, content, children, size = 'small',
  closeIcon, closeLabel = 'Close dialog', className = '', closeOnDimmerClick = true, closeOnEscape = true, returnFocus = true, style, role, centered = true, 'aria-label': ariaLabel, ...props }) {
  const [localOpen, setLocalOpen] = useState(false);
  const opened = open === undefined ? localOpen : Boolean(open);
  const isOpenRef = useRef(opened);
  // Capture before the focus trap's timer, and only on open/close. Passive
  // effects can run after focus moves in React 19; stack changes must not
  // replace the opener when a nested dialog suspends this dialog's trap.
  useLayoutEffect(() => {
    isOpenRef.current = opened;
    if (!opened || !returnFocus) return undefined;
    // Keep the owning realm: this callback can outlive the component and
    // jsdom's global document during test teardown.
    const ownerDocument = document;
    const ownerWindow = ownerDocument.defaultView;
    const opener = ownerDocument.activeElement;
    return () => {
      isOpenRef.current = false;
      const timer = ownerWindow.setTimeout(() => {
        ownerDocument.removeEventListener('keydown', cancelOnTab);
        if (!isOpenRef.current && opener?.isConnected) opener.focus?.({ preventScroll: true });
      }, 10);
      function cancelOnTab(event) {
        if (event.key !== 'Tab') return;
        ownerWindow.clearTimeout(timer);
        ownerDocument.removeEventListener('keydown', cancelOnTab);
      }
      ownerDocument.addEventListener('keydown', cancelOnTab);
    };
  }, [opened, returnFocus]);
  const id = useId();
  const stack = useContext(DialogStackContext);
  const { add, remove } = stack;
  // Settle the active trap before browser focus-return timers run.
  useLayoutEffect(() => {
    if (opened) add(id);
    return () => remove(id);
  }, [opened, id, add, remove]);
  const top = stack.ids.length === 0 || stack.ids.at(-1) === id;
  const close = () => { setLocalOpen(false); onClose?.(); };
  const parts = Children.toArray(children);
  const heading = parts.find((child) => isValidElement(child) && child.type === Header);
  const title = heading?.props.children ?? header;
  const fullScreen = style?.width === '100vw';
  const showClose = closeIcon !== false && Boolean(closeIcon || onClose || trigger);
  const activate = (event) => { trigger?.props.onClick?.(event); if (!event.defaultPrevented) setLocalOpen(true); };
  // Image triggers need a keyboard-operable control; buttons retain their refs.
  const opener = trigger && (trigger.type === 'button' || trigger.type === Button
    ? cloneElement(trigger, { onClick: activate, 'aria-haspopup': 'dialog', 'aria-label': trigger.props['aria-label'] || (trigger.props.children || trigger.props.content ? undefined : typeof title === 'string' ? title : undefined) })
    : <UnstyledButton onClick={activate} aria-haspopup="dialog" aria-label={typeof title === 'string' ? title : 'Open details'}>{trigger}</UnstyledButton>);
  return <>{opener}<MantineModal.Root {...props} opened={opened} onClose={close}
    size={({ mini: 360, tiny: 420, small: 620, large: 1000, fullscreen: '100%' })[size] || size}
    fullScreen={fullScreen} centered={centered && !fullScreen} zIndex={2001 + Math.max(0, stack.ids.indexOf(id)) * 2}
    closeOnClickOutside={top && closeOnDimmerClick} closeOnEscape={top && closeOnEscape}
    trapFocus={top} returnFocus={false} transitionProps={{ duration: 0 }} className="tour-ui">
    {top && <MantineModal.Overlay backgroundOpacity={0.45} />}
    {/* Content's className/style also reach Mantine's fixed inner container.
        Scope card layout to content so short dialogs stay centered. */}
    <MantineModal.Content role={role} aria-label={ariaLabel}
      classNames={{ content: `tour-dialog ${className}` }} styles={{ content: style }}>
      {(title || showClose) && <MantineModal.Header style={heading?.props.style}>
        {title && (ariaLabel ? <Text component="div" className="tour-dialog-title">{title}</Text> : <MantineModal.Title component="div" className="tour-dialog-title">{title}</MantineModal.Title>)}
        {showClose && <MantineModal.CloseButton aria-label={closeLabel} style={{ color: heading?.props.style?.color }} />}
      </MantineModal.Header>}
      <MantineModal.Body p={0}>{content != null ? <Content>{content}</Content> : parts.filter((child) => child !== heading)}</MantineModal.Body>
    </MantineModal.Content>
  </MantineModal.Root></>;
}
Dialog.Header = Header;
Dialog.Content = Content;
Dialog.Actions = Actions;
